// ./src/components/features/Submissions/SubmissionDetailsDrawer/CoordinatesFieldValue.tsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  Paper,
  ActionIcon,
  Tooltip,
  CopyButton,
  Skeleton,
} from "@mantine/core";
import { AppIcon } from "@/components/generic";
import { FormFieldTypeIcon } from "@/components/features";
import { useGoogleMapsContext } from "@/contexts";
import { parseOptions } from "@/utils/parserHelpers";
import { FIELD_TYPE_IDS } from "@/config";

// ============================================================================
// Types
// ============================================================================

interface CoordinatesFieldOptions {
  format?: "decimal" | "dms";
  decimalPrecision?: number;
  allowedRegions?: string[];
  boundingBox?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  polygons?: Array<{
    title: string;
    points: Array<{ lat: number; lng: number }>;
  }>;
  radiusCenter?: { lat: number; lng: number };
  radiusKm?: number;
}

interface ParsedCoordinates {
  lat: number;
  lng: number;
}

export interface CoordinatesFieldValueProps {
  /** Field label */
  label: string;
  /** Raw value from form submission */
  value: any;
  /** Field options JSON string */
  fieldOptions?: string;
  /** Hide copy button */
  hideCopyButton?: boolean;
  /** Whether this is an orphaned field */
  isOrphaned?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_OPTIONS: CoordinatesFieldOptions = {
  format: "decimal",
  decimalPrecision: 6,
};

const MAP_HEIGHT = 200;

// Polygon colors matching the editor
const POLYGON_COLORS = [
  "#4CAF50",
  "#2196F3",
  "#FF9800",
  "#9C27B0",
  "#F44336",
  "#00BCD4",
  "#FFEB3B",
  "#795548",
  "#607D8B",
  "#E91E63",
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse coordinates from various formats
 */
const parseCoordinates = (value: any): ParsedCoordinates | null => {
  if (!value) return null;

  // Already an object with lat/lng
  if (typeof value === "object" && value !== null) {
    const lat = parseFloat(value.lat ?? value.latitude);
    const lng = parseFloat(value.lng ?? value.lon ?? value.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return null;
  }

  // String format: "lat,lng" or "lat, lng"
  if (typeof value === "string") {
    // Try comma-separated format
    const parts = value.split(",").map((p) => p.trim());
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }

    // Try DMS format (basic parsing)
    // Format: 45°25'17.4"N, 75°41'49.9"W
    const dmsMatch = value.match(
      /(-?\d+)[°]?\s*(\d+)?[']?\s*(\d+\.?\d*)?[""]?\s*([NSns])?\s*[,\s]+\s*(-?\d+)[°]?\s*(\d+)?[']?\s*(\d+\.?\d*)?[""]?\s*([EWew])?/
    );
    if (dmsMatch) {
      const latDeg = parseFloat(dmsMatch[1]) || 0;
      const latMin = parseFloat(dmsMatch[2]) || 0;
      const latSec = parseFloat(dmsMatch[3]) || 0;
      const latDir = (dmsMatch[4] || "N").toUpperCase();

      const lngDeg = parseFloat(dmsMatch[5]) || 0;
      const lngMin = parseFloat(dmsMatch[6]) || 0;
      const lngSec = parseFloat(dmsMatch[7]) || 0;
      const lngDir = (dmsMatch[8] || "E").toUpperCase();

      let lat = latDeg + latMin / 60 + latSec / 3600;
      let lng = lngDeg + lngMin / 60 + lngSec / 3600;

      if (latDir === "S") lat = -lat;
      if (lngDir === "W") lng = -lng;

      return { lat, lng };
    }
  }

  return null;
};

/**
 * Format coordinates for display
 */
const formatCoordinates = (
  coords: ParsedCoordinates,
  format: "decimal" | "dms" = "decimal",
  precision: number = 6
): string => {
  if (format === "dms") {
    const formatDMS = (value: number, isLat: boolean): string => {
      const absolute = Math.abs(value);
      const degrees = Math.floor(absolute);
      const minutesFloat = (absolute - degrees) * 60;
      const minutes = Math.floor(minutesFloat);
      const seconds = ((minutesFloat - minutes) * 60).toFixed(2);
      const direction = isLat
        ? value >= 0
          ? "N"
          : "S"
        : value >= 0
        ? "E"
        : "W";
      return `${degrees}°${minutes}'${seconds}"${direction}`;
    };
    return `${formatDMS(coords.lat, true)}, ${formatDMS(coords.lng, false)}`;
  }

  return `${coords.lat.toFixed(precision)}, ${coords.lng.toFixed(precision)}`;
};

/**
 * Get Google Maps URL for coordinates
 */
const getGoogleMapsUrl = (coords: ParsedCoordinates): string => {
  return `https://www.google.com/maps?q=${coords.lat},${coords.lng}`;
};

// ============================================================================
// Mini Map Component
// ============================================================================

interface MiniMapProps {
  coordinates: ParsedCoordinates;
  options: CoordinatesFieldOptions;
}

const MiniMap: React.FC<MiniMapProps> = ({ coordinates, options }) => {
  const { loadLibraries, getCoreLibrary } = useGoogleMapsContext();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for map objects
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const circleRef = useRef<google.maps.Circle | null>(null);
  const polygonsRef = useRef<google.maps.Polygon[]>([]);
  const rectangleRef = useRef<google.maps.Rectangle | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      if (!mapContainerRef.current) return;

      try {
        const libs = await loadLibraries(["maps", "marker"]);

        if (!isMounted || !mapContainerRef.current) return;

        // Get core library for constructors
        const mapsCore = getCoreLibrary();
        if (!mapsCore) {
          throw new Error("Google Maps core library not available");
        }

        // Create map
        const map = new libs.maps.Map(mapContainerRef.current, {
          center: coordinates,
          zoom: 14,
          mapId: "coordinates-mini-map",
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
        });

        mapRef.current = map;

        // Add marker for the submitted coordinates
        const markerElement = document.createElement("div");
        markerElement.innerHTML = `
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#E53935"/>
            <circle cx="12" cy="9" r="2" fill="white"/>
          </svg>
        `;
        markerElement.style.cursor = "pointer";

        const marker = new libs.marker.AdvancedMarkerElement({
          position: coordinates,
          map: map,
          content: markerElement,
          title: "Submitted location",
        });
        markerRef.current = marker;

        // Calculate bounds to fit all elements
        const bounds = new mapsCore.LatLngBounds();
        bounds.extend(coordinates);

        // Add radius circle if configured
        if (options.radiusCenter && options.radiusKm) {
          const circle = new mapsCore.Circle({
            center: options.radiusCenter,
            radius: options.radiusKm * 1000, // Convert km to meters
            map: map,
            strokeColor: "#2196F3",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#2196F3",
            fillOpacity: 0.1,
          });
          circleRef.current = circle;

          // Extend bounds to include circle
          const circleBounds = circle.getBounds();
          if (circleBounds) {
            bounds.union(circleBounds);
          }

          // Add center marker for radius
          const centerElement = document.createElement("div");
          centerElement.style.width = "10px";
          centerElement.style.height = "10px";
          centerElement.style.backgroundColor = "#2196F3";
          centerElement.style.border = "2px solid white";
          centerElement.style.borderRadius = "50%";
          centerElement.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";

          new libs.marker.AdvancedMarkerElement({
            position: options.radiusCenter,
            map: map,
            content: centerElement,
            title: "Radius center",
          });
        }

        // Add bounding box if configured
        if (options.boundingBox) {
          const rectangle = new mapsCore.Rectangle({
            bounds: {
              north: options.boundingBox.north,
              south: options.boundingBox.south,
              east: options.boundingBox.east,
              west: options.boundingBox.west,
            },
            map: map,
            strokeColor: "#FF9800",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#FF9800",
            fillOpacity: 0.1,
          });
          rectangleRef.current = rectangle;

          // Extend bounds
          const rectBounds = rectangle.getBounds();
          if (rectBounds) {
            bounds.union(rectBounds);
          }
        }

        // Add polygons if configured
        if (options.polygons && options.polygons.length > 0) {
          options.polygons.forEach((polygonData, index) => {
            if (polygonData.points && polygonData.points.length >= 3) {
              const color = POLYGON_COLORS[index % POLYGON_COLORS.length];
              const polygon = new mapsCore.Polygon({
                paths: polygonData.points,
                map: map,
                strokeColor: color,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: color,
                fillOpacity: 0.15,
              });
              polygonsRef.current.push(polygon);

              // Extend bounds
              polygonData.points.forEach((point) => bounds.extend(point));
            }
          });
        }

        // Fit map to bounds if we have restrictions
        const hasRestrictions =
          options.radiusCenter ||
          options.boundingBox ||
          (options.polygons && options.polygons.length > 0);

        if (hasRestrictions) {
          map.fitBounds(bounds, 20);
        }

        setIsLoading(false);
      } catch (err) {
        console.error("Failed to load map:", err);
        if (isMounted) {
          setError("Failed to load map");
          setIsLoading(false);
        }
      }
    };

    initMap();

    return () => {
      isMounted = false;

      // Cleanup
      if (markerRef.current) {
        markerRef.current.map = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
      if (rectangleRef.current) {
        rectangleRef.current.setMap(null);
      }
      polygonsRef.current.forEach((p) => p.setMap(null));
      polygonsRef.current = [];
      mapRef.current = null;
    };
  }, [coordinates, options, loadLibraries, getCoreLibrary]);

  if (error) {
    return (
      <Paper
        withBorder
        h={MAP_HEIGHT}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        <Stack align="center" gap="xs">
          <AppIcon iconName="MapOff" size={24} color="gray" />
          <Text size="xs" c="dimmed">
            {error}
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box pos="relative">
      {isLoading && <Skeleton height={MAP_HEIGHT} radius="sm" />}
      <Box
        ref={mapContainerRef}
        h={MAP_HEIGHT}
        style={{
          borderRadius: "var(--mantine-radius-sm)",
          overflow: "hidden",
          display: isLoading ? "none" : "block",
        }}
      />
    </Box>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CoordinatesFieldValue: React.FC<CoordinatesFieldValueProps> = ({
  label,
  value,
  fieldOptions,
  hideCopyButton = false,
  isOrphaned = false,
}) => {
  const coordinates = useMemo(() => parseCoordinates(value), [value]);
  const options = useMemo(
    () => parseOptions(fieldOptions, DEFAULT_OPTIONS),
    [fieldOptions]
  );

  const formattedValue = useMemo(() => {
    if (!coordinates) return null;
    return formatCoordinates(
      coordinates,
      options.format,
      options.decimalPrecision
    );
  }, [coordinates, options.format, options.decimalPrecision]);

  const googleMapsUrl = useMemo(() => {
    if (!coordinates) return null;
    return getGoogleMapsUrl(coordinates);
  }, [coordinates]);

  const copyValue = useMemo(() => {
    if (!coordinates) return "";
    return `${coordinates.lat}, ${coordinates.lng}`;
  }, [coordinates]);

  // Check if we have any geo restrictions to show
  const hasGeoRestrictions = useMemo(() => {
    return !!(
      options.radiusCenter ||
      options.boundingBox ||
      (options.polygons && options.polygons.length > 0)
    );
  }, [options]);

  // Icon section
  const IconSection = (
    <FormFieldTypeIcon
      fieldType={FIELD_TYPE_IDS.COORDINATES}
      size={16}
      color={
        isOrphaned
          ? "var(--mantine-color-yellow-6)"
          : "var(--mantine-color-gray-5)"
      }
    />
  );

  // Empty state
  if (!coordinates) {
    return (
      <Box>
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
        <Paper withBorder p="sm">
          <Group gap="xs">
            {IconSection}
            <Text c="dimmed" size="sm">
              —
            </Text>
          </Group>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Text size="sm" fw={500} mb={4}>
        {label}
      </Text>

      <Stack gap="xs">
        {/* Coordinates display */}
        <Paper withBorder p="sm">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              {IconSection}
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} style={{ fontFamily: "monospace" }}>
                  {formattedValue}
                </Text>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    Lat: {coordinates.lat.toFixed(6)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    •
                  </Text>
                  <Text size="xs" c="dimmed">
                    Lng: {coordinates.lng.toFixed(6)}
                  </Text>
                </Group>
              </Stack>
            </Group>

            <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
              {/* Open in Google Maps */}
              {googleMapsUrl && (
                <Tooltip label="Open in Google Maps" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    size="sm"
                    component="a"
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppIcon iconName="ExternalLink" size={14} />
                  </ActionIcon>
                </Tooltip>
              )}

              {/* Copy button */}
              {!hideCopyButton && (
                <CopyButton value={copyValue}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied!" : "Copy coordinates"}
                      withArrow
                    >
                      <ActionIcon
                        variant="subtle"
                        color={copied ? "teal" : "gray"}
                        size="sm"
                        onClick={copy}
                      >
                        <AppIcon
                          iconName={copied ? "Check" : "Copy"}
                          size={14}
                        />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              )}
            </Group>
          </Group>
        </Paper>

        {/* Mini map */}
        <MiniMap coordinates={coordinates} options={options} />

        {/* Restriction info badges */}
        {hasGeoRestrictions && (
          <Group gap="xs">
            {options.radiusCenter && options.radiusKm && (
              <Paper withBorder px="xs" py={4}>
                <Group gap={4}>
                  <AppIcon iconName="Circle" size={12} color="blue" />
                  <Text size="xs" c="dimmed">
                    Radius: {options.radiusKm} km
                  </Text>
                </Group>
              </Paper>
            )}
            {options.boundingBox && (
              <Paper withBorder px="xs" py={4}>
                <Group gap={4}>
                  <AppIcon iconName="Square" size={12} color="orange" />
                  <Text size="xs" c="dimmed">
                    Bounding box
                  </Text>
                </Group>
              </Paper>
            )}
            {options.polygons && options.polygons.length > 0 && (
              <Paper withBorder px="xs" py={4}>
                <Group gap={4}>
                  <AppIcon iconName="Pentagon" size={12} color="green" />
                  <Text size="xs" c="dimmed">
                    {options.polygons.length} region
                    {options.polygons.length !== 1 ? "s" : ""}
                  </Text>
                </Group>
              </Paper>
            )}
          </Group>
        )}
      </Stack>
    </Box>
  );
};
